import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import MaterialIcon from '../ui/MaterialIcon'
import { downloadUserDataExport } from '../../lib/exportUserData'
import {
  isDeletionConfirmationValid,
  requestDataDeletion,
  type DeletionScope,
} from '../../lib/deleteUserData'
import { usePrivacyCopy } from '../../hooks/usePrivacyCopy'

type PrivacySettingsSheetProps = {
  onClose: () => void
  onAccountDeleted: () => void
  onWellnessDataDeleted: () => void
}

type DeleteConfirmModalProps = {
  scope: DeletionScope
  onClose: () => void
  onSuccess: () => void
}

function DeleteConfirmModal({ scope, onClose, onSuccess }: DeleteConfirmModalProps) {
  const { t } = useTranslation()
  const { deletionScopes } = usePrivacyCopy()
  const details = deletionScopes[scope]
  const [confirmed, setConfirmed] = useState(false)
  const [confirmationText, setConfirmationText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const canSubmit = confirmed && isDeletionConfirmationValid(confirmationText) && !busy

  const handleSubmit = async () => {
    if (!canSubmit) return
    setBusy(true)
    setError('')

    const result = await requestDataDeletion(scope)
    setBusy(false)

    if (!result.ok) {
      setError(result.error ?? t('privacy.deletionFailed'))
      return
    }

    onSuccess()
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border-t border-neutral-800 bg-neutral-950 p-6 pb-10">
        <div className="mx-auto mb-6 h-1 w-10 rounded-full bg-neutral-700" />
        <h2 className="text-lg font-semibold text-white">{details.title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-neutral-400">{details.summary}</p>

        <ul className="mt-4 space-y-2">
          {details.bullets.map((item) => (
            <li key={item} className="flex items-start gap-2 text-xs text-neutral-400">
              <MaterialIcon name="remove" size="sm" className="mt-0.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>

        {details.retentionNote ? (
          <p className="mt-4 rounded-xl border border-neutral-800 bg-neutral-900 p-3 text-[11px] leading-relaxed text-neutral-500">
            {details.retentionNote}
          </p>
        ) : null}

        <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-neutral-800 bg-neutral-900 p-3">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            className="mt-0.5"
          />
          <span className="text-xs leading-relaxed text-neutral-300">{t('privacy.confirmPermanent')}</span>
        </label>

        <div className="mt-4">
          <label htmlFor="delete-confirmation" className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            {t('privacy.typeDelete')}
          </label>
          <input
            id="delete-confirmation"
            type="text"
            value={confirmationText}
            onChange={(event) => setConfirmationText(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder={t('privacy.deletePlaceholder')}
            className="mt-2 w-full rounded-xl border border-neutral-700 bg-neutral-900 px-4 py-3 text-sm text-white outline-none focus:border-red-500/60"
          />
        </div>

        {error ? <p className="mt-3 text-sm text-red-400">{error}</p> : null}

        <div className="mt-6 space-y-2">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
            className="w-full rounded-2xl bg-red-600 py-3.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? t('privacy.deleting') : details.title}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="w-full rounded-2xl border border-neutral-700 bg-neutral-900 py-3 text-sm font-semibold text-white"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export function PrivacySettingsSheet({
  onClose,
  onAccountDeleted,
  onWellnessDataDeleted,
}: PrivacySettingsSheetProps) {
  const { t } = useTranslation()
  const [pendingScope, setPendingScope] = useState<DeletionScope | null>(null)
  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [exportOk, setExportOk] = useState<string | null>(null)

  const handleExport = async () => {
    setExportBusy(true)
    setExportError(null)
    setExportOk(null)

    const result = await downloadUserDataExport()
    setExportBusy(false)

    if (!result.ok) {
      setExportError(result.error ?? t('privacy.exportFailed'))
      return
    }

    setExportOk(t('privacy.exportOk'))
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm">
        <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border-t border-neutral-800 bg-neutral-950 p-6 pb-10">
          <div className="mx-auto mb-6 h-1 w-10 rounded-full bg-neutral-700" />
          <h2 className="text-lg font-semibold text-white">{t('privacy.title')}</h2>
          <p className="mt-2 text-sm leading-relaxed text-neutral-400">{t('compliance.dataUsage')}</p>

          <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t('privacy.gdprHeading')}</p>
            <p className="mt-2 text-xs leading-relaxed text-neutral-400">{t('privacy.gdprBody')}</p>
          </div>

          <div className="mt-6 space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t('privacy.exportHeading')}</p>
            <button
              type="button"
              disabled={exportBusy}
              className="flex w-full items-center justify-between rounded-xl border border-kilo-border bg-neutral-900 px-4 py-3 text-left text-sm text-white disabled:opacity-50"
              onClick={() => void handleExport()}
            >
              <span>
                {t('privacy.exportButton')}
                <span className="mt-0.5 block text-xs text-neutral-500">{t('privacy.exportHint')}</span>
              </span>
              <MaterialIcon name={exportBusy ? 'hourglass_empty' : 'download'} size="sm" />
            </button>
            {exportError ? (
              <p className="rounded-xl border border-red-900/50 bg-red-950/40 px-3 py-2 text-sm text-red-400">
                {exportError}
              </p>
            ) : null}
            {exportOk ? (
              <p className="rounded-xl border border-kilo-accent/30 bg-kilo-accent/5 px-3 py-2 text-sm text-kilo-accent">
                {exportOk}
              </p>
            ) : null}
          </div>

          <div className="mt-6 space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t('privacy.deleteHeading')}</p>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-xl border border-kilo-border bg-neutral-900 px-4 py-3 text-left text-sm text-white"
              onClick={() => setPendingScope('wellness_data')}
            >
              <span>
                {t('privacy.deleteWellness')}
                <span className="mt-0.5 block text-xs text-neutral-500">{t('privacy.deleteWellnessHint')}</span>
              </span>
              <MaterialIcon name="chevron_right" size="sm" />
            </button>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-xl border border-red-900/40 bg-red-950/20 px-4 py-3 text-left text-sm text-red-300"
              onClick={() => setPendingScope('account')}
            >
              <span>
                {t('privacy.deleteAccount')}
                <span className="mt-0.5 block text-xs text-red-400/80">{t('privacy.deleteAccountHint')}</span>
              </span>
              <MaterialIcon name="chevron_right" size="sm" />
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="mt-6 w-full rounded-2xl border border-neutral-700 bg-neutral-900 py-3 text-sm font-semibold text-white"
          >
            {t('common.close')}
          </button>
        </div>
      </div>

      {pendingScope ? (
        <DeleteConfirmModal
          scope={pendingScope}
          onClose={() => setPendingScope(null)}
          onSuccess={() => {
            const deletedScope = pendingScope
            setPendingScope(null)
            onClose()
            if (deletedScope === 'account') onAccountDeleted()
            else onWellnessDataDeleted()
          }}
        />
      ) : null}
    </>
  )
}
